import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { ResourceAvailabilityPeriod } from '@/types/scheduling-api';
import { localNoZ } from '@/scheduling/lib/device-availability';

/**
 * Device presence / availability — PROD-2007.
 *
 *   GET /api/Slots/ResourceAvailability?resourceId=&practiceId=&from=&to=
 *
 * Scheduling (live on dev): the device's presence (its own working hours, from
 * PracticeManagement `GetActorPresence(Device)`) minus every slot on its
 * schedule, plus the overbooking allowance. `from` / `to` bind to `DateTime`
 * and are converted from the caller's `UserTimeZone`, so they travel as LOCAL
 * wall clock without a `Z`; the periods come back already moved into that
 * zone (see `device-availability.ts` for why they are parsed textually).
 *
 * A device with no working hours answers `[]` → the column shows no shading
 * and a "Not bookable" marker (ruling 2026-09-12).
 */

export interface ResourceAvailabilityParams {
  deviceId: string;
  practiceId: string;
  /** Local instants; formatted `YYYY-MM-DDTHH:mm:ss`. */
  from: Date;
  to: Date;
}

export const RESOURCE_AVAILABILITY_KEY = 'resource-availability';

/**
 * NO CACHE — Andrew, 2026-09-21: "don't cache the calendar slots at all"
 * (PROD-2444).
 *
 * The app-wide default is `staleTime: 5 * 60_000` with `refetchOnWindowFocus`
 * and `refetchOnReconnect` both OFF, which is right for a service catalogue
 * and wrong for this: a slot feed answers *what is free right now*, and two
 * operators booking the same machine minutes apart is the case a five-minute
 * memory loses. `refetchOnMount: 'always'` alone only covers a fresh mount.
 *
 *   staleTime 0            — never reuse an answer without asking again
 *   gcTime 0               — drop it the moment nothing is watching, so a
 *                            remount shows a loading state rather than
 *                            painting yesterday's windows and correcting
 *                            itself a beat later
 *   refetchOnMount 'always'
 *   refetchOnWindowFocus / refetchOnReconnect — re-read after the operator
 *                            comes back from another tab or a dropped link,
 *                            overriding the global opt-outs on purpose
 *
 * `gcTime: 0` is safe for the consumers that gate with `enabled` rather than
 * by unmounting (the Devices tab badge, PROD-2364): a disabled query still has
 * a mounted observer, so nothing is collected while the page is on screen.
 */
const NO_CACHE = {
  staleTime: 0,
  gcTime: 0,
  refetchOnMount: 'always',
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
} as const;


export function resourceAvailabilityQueryOptions(params: ResourceAvailabilityParams, enabled = true) {
  const fromISO = localNoZ(params.from);
  const toISO = localNoZ(params.to);
  return queryOptions<ResourceAvailabilityPeriod[]>({
    queryKey: [RESOURCE_AVAILABILITY_KEY, params.deviceId, params.practiceId, fromISO, toISO],
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams({
        resourceId: params.deviceId,
        practiceId: params.practiceId,
        from: fromISO,
        to: toISO,
      });
      const res = await get<ResourceAvailabilityPeriod[] | null>(
        `/api/Slots/ResourceAvailability?${qs.toString()}`,
        { signal },
      );
      // `null` / missing body → treat as "no windows" so React Query never
      // sees `undefined` (which it rejects) and the column reads not bookable.
      return Array.isArray(res) ? res : [];
    },
    enabled: enabled && !!params.deviceId && !!params.practiceId,
    // Was `staleTime: 30_000` — thirty seconds of "this window is free" is
    // still thirty seconds in which it is not.
    ...NO_CACHE,
    // One failing device must not toast once per column; the grid shows a
    // neutral header for a device whose availability is unknown.
    meta: { skipGlobalError: true },
  });
}

/** Availability of ONE device (the Book-a-device flow; exposes `refetch`). */
export function useResourceAvailability(params: ResourceAvailabilityParams | null) {
  return useQuery(
    resourceAvailabilityQueryOptions(
      params ?? { deviceId: '', practiceId: '', from: new Date(0), to: new Date(0) },
      !!params,
    ),
  );
}

export interface DeviceAvailabilityMap {
  /** deviceId → periods (`undefined` while loading / on error, `[]` = not bookable). */
  byDevice: Map<string, ResourceAvailabilityPeriod[] | undefined>;
  isLoading: boolean;
}

/**
 * Availability of every visible device column for the shown range — one
 * request per device, in parallel, cached per device+range so paging days
 * or toggling the subtype filter only fetches what is new.
 */
export function useDeviceAvailability(params: {
  practiceId: string;
  deviceIds: readonly string[];
  from: Date;
  to: Date;
  enabled?: boolean;
}): DeviceAvailabilityMap {
  const enabled = params.enabled ?? true;
  const results = useQueries({
    queries: params.deviceIds.map((deviceId) =>
      resourceAvailabilityQueryOptions(
        { deviceId, practiceId: params.practiceId, from: params.from, to: params.to },
        enabled,
      ),
    ),
  });
  const byDevice = new Map<string, ResourceAvailabilityPeriod[] | undefined>();
  params.deviceIds.forEach((id, i) => byDevice.set(id, results[i]?.data));
  return { byDevice, isLoading: results.some((r) => r.isLoading) };
}
